import { Controller } from "@hotwired/stimulus"

/**
 * Progress Bar Controller
 *
 * Manages animated progress bars with proper width calculation.
 * Prevents Turbo caching issues by re-applying the correct width
 * after Turbo Stream replacements and page loads.
 */
export default class extends Controller {
  static values = {
    width: Number,
    animated: { type: Boolean, default: false }
  }

  connect() {
    // Apply width on initial connect
    this.applyWidth()

    // Listen for Turbo events to re-apply width after updates
    this.boundTurboListener = this.handleTurboUpdate.bind(this)
    document.addEventListener('turbo:frame-render', this.boundTurboListener)
    document.addEventListener('turbo:render', this.boundTurboListener)
    document.addEventListener('turbo:before-cache', this.handleBeforeCache.bind(this))
  }

  disconnect() {
    // Cleanup event listeners
    if (this.boundTurboListener) {
      document.removeEventListener('turbo:frame-render', this.boundTurboListener)
      document.removeEventListener('turbo:render', this.boundTurboListener)
    }
  }

  /**
   * Apply the correct width to the progress bar
   */
  applyWidth() {
    const targetWidth = this.widthValue

    if (this.animatedValue && !this.element.dataset.progressInitialized) {
      // Animate from 0 to target width (only on first load)
      this.element.style.width = '0%'

      // Force reflow
      void this.element.offsetWidth

      // Animate to target
      requestAnimationFrame(() => {
        this.element.style.width = `${targetWidth}%`
        this.element.dataset.progressInitialized = 'true'
      })
    } else {
      // Set width immediately (for cached or updated content)
      this.element.style.width = `${targetWidth}%`
    }

    // Update ARIA for accessibility
    this.element.setAttribute('aria-valuenow', targetWidth)
  }

  /**
   * Handle Turbo updates by re-applying width
   */
  handleTurboUpdate(event) {
    // Only re-apply if this element is still in the DOM
    if (this.element.isConnected) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.applyWidth()
      })
    }
  }

  /**
   * Before Turbo caches the page, reset to ensure fresh state
   */
  handleBeforeCache() {
    // Reset the initialized flag so animation works on back/forward
    delete this.element.dataset.progressInitialized
  }

  /**
   * Update width value (called automatically by Stimulus when value changes)
   */
  widthValueChanged() {
    this.applyWidth()
  }
}
